import { useState, useMemo, useCallback, useRef } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Typography,
} from '@execonline-inc/execonline-ui';
import { observer } from 'mobx-react';
import { fromNullable, just, Maybe, nothing } from 'maybeasy';
import { T } from '../../../../../../../Translations';
import { LearningDesign } from '../../Types/SectionTemplates';
import { ExperienceResourceArray, ExperienceResource } from '../../Types';
import { useFetchMultipleExperienceDetails } from './useFetchMultipleExperienceDetails';
import { deriveCompetencyFilterOptions, deriveProductTypeFilterOptions } from './Filters/Utils';
import { filterCuratedSections } from './Filters/FilteringLogic';
import ListView from './ListView';
import DetailView from './DetailView';
import { TabType } from './TabNavigation';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApply: (selectedIds: number[]) => void;
  curatedSections: LearningDesign[];
  isLoading: boolean;
  experiencesArray: ExperienceResourceArray;
}

type ViewMode = 'list' | 'detail';

function CuratedSectionsModal({
  isOpen,
  onClose,
  onApply,
  curatedSections,
  isLoading,
  experiencesArray,
}: Props) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [detailSectionId, setDetailSectionId] = useState<Maybe<number>>(nothing());
  const [activeTab, setActiveTab] = useState<TabType>('programs');
  const [selectedCompetencies, setSelectedCompetencies] = useState<string[]>([]);
  const [selectedProductTypes, setSelectedProductTypes] = useState<string[]>([]);
  const [activeCompetencyId, setActiveCompetencyId] = useState<Maybe<number>>(nothing());

  // Handlers
  const handleToggleSelection = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id],
    );
  };

  const handleViewDetail = (id: number) => {
    setDetailSectionId(just(id));
    setViewMode('detail');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setDetailSectionId(nothing());
    setActiveTab('programs');
  };

  const handleRemoveFromSelection = () => {
    detailSectionId.do((id) => {
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
    });
  };

  const handleApply = () => {
    onApply(selectedIds);
    setSelectedIds([]);
    setViewMode('list');
    setDetailSectionId(nothing());
  };

  const handleCancel = () => {
    onClose();
    setSelectedIds([]);
    setViewMode('list');
    setDetailSectionId(nothing());
  };

  // Derived state
  const selectedCount = selectedIds.length;
  const detailSection: Maybe<LearningDesign> = detailSectionId.andThen((id) =>
    fromNullable(curatedSections.find((s) => s.id === id)),
  );
  const isDetailSectionSelected = detailSectionId
    .map((id) => selectedIds.includes(id))
    .getOrElse(() => false);

  const emptyFragment = () => <></>;

  // Get basic experiences for the detail section
  const basicExperiences = useMemo(
    () =>
      detailSection
        .map((section) =>
          section.experiences
            .map((expId) => experiencesArray.find((e) => e.payload.id === expId))
            .filter((exp): exp is ExperienceResource => exp !== undefined),
        )
        .getOrElse(() => []),
    [detailSection, experiencesArray],
  );

  // Filter options
  const competencyFilterOptions = useMemo(
    () => deriveCompetencyFilterOptions(curatedSections, experiencesArray),
    [curatedSections, experiencesArray],
  );

  const productTypeFilterOptions = useMemo(
    () => deriveProductTypeFilterOptions(curatedSections, experiencesArray),
    [curatedSections, experiencesArray],
  );

  // Filter initialization (adjust during render pattern)
  const competencyFiltersInitialized = useRef(false);
  const productTypeFiltersInitialized = useRef(false);

  if (!competencyFiltersInitialized.current && competencyFilterOptions.length > 0) {
    setSelectedCompetencies(competencyFilterOptions.map((o) => o.name));
    competencyFiltersInitialized.current = true;
  }

  if (!productTypeFiltersInitialized.current && productTypeFilterOptions.length > 0) {
    setSelectedProductTypes(productTypeFilterOptions.map((o) => o.name));
    productTypeFiltersInitialized.current = true;
  }

  // Set first competency as active when switching to competencies tab
  // Called directly in the tab change handler instead of an effect
  const handleTabChange = useCallback(
    (tab: TabType) => {
      setActiveTab(tab);
      if (tab === 'competencies') {
        detailSection.do((section) => {
          if (section.competencies.length > 0) {
            setActiveCompetencyId(just(section.competencies[0].id));
          }
        });
      }
    },
    [detailSection],
  );

  // Filtered sections for list view
  const filteredCuratedSections = useMemo(
    () =>
      filterCuratedSections(
        curatedSections,
        selectedCompetencies,
        selectedProductTypes,
        experiencesArray,
      ),
    [curatedSections, selectedCompetencies, selectedProductTypes, experiencesArray],
  );

  // Fetch details for experiences (only in detail view)
  const {
    loading: loadingDetails,
    combinedExperiences,
    error: detailsError,
  } = useFetchMultipleExperienceDetails(viewMode === 'detail' ? basicExperiences : []);

  return (
    <Modal
      size="6xl"
      open={isOpen}
      onClose={handleCancel}
      outsidePress={false}
      classNames={{ closeButton: 'hidden' }}
      className="flex flex-col !max-w-[1200px] h-[calc(100vh-4rem)] overflow-hidden"
      data-intercom-target="curated-sections-modal"
    >
      <ModalHeader className="w-full flex-col items-start gap-2">
        {viewMode === 'detail' ? (
          detailSection
            .map((section) => (
              <>
                <Button
                  variant="text"
                  color="secondary"
                  onClick={handleBackToList}
                  className="p-0 mb-2"
                  data-intercom-target="curated-section-back-to-list-button"
                >
                  <span className="flex items-center gap-1">
                    <span>←</span>
                    <T kind="Back to Curated Sections" />
                  </span>
                </Button>
                <div className="w-full flex justify-between items-start">
                  <div>
                    <h2 className="text-3xl font-semibold text-foreground">{section.name}</h2>
                    <div className="pt-1 font-normal text-base text-muted-foreground">
                      {section.description}
                    </div>
                  </div>
                  <Button
                    variant="outlined"
                    color={isDetailSectionSelected ? 'error' : 'primary'}
                    onClick={
                      isDetailSectionSelected
                        ? handleRemoveFromSelection
                        : () => handleToggleSelection(section.id)
                    }
                    data-intercom-target="curated-section-toggle-selection-button"
                  >
                    <T
                      kind={isDetailSectionSelected ? 'Remove from Selection' : 'Add to Selection'}
                    />
                  </Button>
                </div>
              </>
            ))
            .getOrElse(emptyFragment)
        ) : (
          <div className="w-full flex justify-between items-start gap-4">
            <div className="max-w-[70%]">
              <h2 className="text-3xl font-semibold text-foreground">
                <T kind="Select a curated section" />
              </h2>
              <div className="pt-1 font-normal text-base text-muted-foreground">
                <T kind="Choose one of the curated collections below, or create your own. You can edit the final list of programs in the next step." />
              </div>
            </div>
            <Button
              variant="outlined"
              onClick={handleCancel}
              className="whitespace-nowrap flex-shrink-0"
              data-intercom-target="curated-sections-back-to-collection-button"
            >
              <T kind="Back to your Learning Collection" />
            </Button>
          </div>
        )}
      </ModalHeader>

      <ModalBody className="flex-1 flex flex-col overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Typography variant="bodyMedium">
              <T kind="Loading curated sections..." />
            </Typography>
          </div>
        ) : curatedSections.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Typography variant="bodyMedium">
              <T kind="No curated sections available. Please check back later." />
            </Typography>
          </div>
        ) : viewMode === 'list' ? (
          <ListView
            filteredSections={filteredCuratedSections}
            experiencesArray={experiencesArray}
            selectedIds={selectedIds}
            onToggleSelection={handleToggleSelection}
            onViewDetail={handleViewDetail}
            competencyFilterOptions={competencyFilterOptions}
            productTypeFilterOptions={productTypeFilterOptions}
            selectedCompetencies={selectedCompetencies}
            selectedProductTypes={selectedProductTypes}
            onCompetenciesChange={setSelectedCompetencies}
            onProductTypesChange={setSelectedProductTypes}
          />
        ) : (
          detailSection
            .map((section) => (
              <DetailView
                key={section.id}
                section={section}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                basicExperiences={basicExperiences}
                combinedExperiences={combinedExperiences}
                loadingDetails={loadingDetails}
                detailsError={detailsError}
                activeCompetencyId={activeCompetencyId}
                onCompetencyClick={(id) => setActiveCompetencyId(just(id))}
              />
            ))
            .getOrElse(emptyFragment)
        )}
      </ModalBody>

      <ModalFooter className="flex-row justify-end">
        <Button
          variant="text"
          color="secondary"
          onClick={handleCancel}
          data-intercom-target="curated-sections-modal-cancel-button"
        >
          <T kind="Cancel" />
        </Button>
        <Button
          onClick={handleApply}
          disabled={selectedCount === 0 || isLoading}
          data-intercom-target="curated-sections-modal-apply-button"
        >
          <T
            kind={
              selectedCount === 1
                ? 'Continue with 1 Collection'
                : 'Continue with {{count}} Collections'
            }
            count={selectedCount}
          />
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default observer(CuratedSectionsModal);
